/**
 * React hook for tracking online presence of friends
 *
 * Uses P2P peer connections to determine if friends are online.
 * Peer IDs are public keys, so a friend is online if their
 * public key matches a connected peer ID.
 */
import { useMemo, useCallback } from 'react';

interface PeerInfo {
  id: string;
  state: 'discovered' | 'connecting' | 'connected' | 'disconnected';
  lastSeen: number;
}

export type PresenceStatus = 'online' | 'away' | 'offline';

export interface FriendPresence {
  pubkey: string;
  status: PresenceStatus;
  lastSeen: number | null;
  peerState?: PeerInfo['state'];
}

interface UsePresenceResult {
  /** Get presence info for a specific friend */
  getPresence: (pubkey: string) => FriendPresence;
  /** Get all friends with their presence info */
  getFriendsPresence: () => FriendPresence[];
  /** Check if a friend is online */
  isOnline: (pubkey: string) => boolean;
  /** Count of online friends */
  onlineFriendsCount: number;
}

interface UsePresenceOptions {
  /** Set of friend public keys */
  friends: Set<string>;
  /** List of connected/discovered peers */
  peers: PeerInfo[];
}

/**
 * Determine presence status from peer state
 */
function getStatusFromPeerState(state: PeerInfo['state'] | undefined): PresenceStatus {
  if (!state) return 'offline';
  switch (state) {
    case 'connected':
      return 'online';
    case 'connecting':
    case 'discovered':
      return 'away'; // Peer is known but not fully connected
    case 'disconnected':
    default:
      return 'offline';
  }
}

export function usePresence({ friends, peers }: UsePresenceOptions): UsePresenceResult {
  // Build a map of peer ID -> peer info for quick lookup
  const peerMap = useMemo(() => {
    const map = new Map<string, PeerInfo>();
    for (const peer of peers) {
      map.set(peer.id, peer);
    }
    return map;
  }, [peers]);

  // Get presence for a single friend
  const getPresence = useCallback((pubkey: string): FriendPresence => {
    const peer = peerMap.get(pubkey);
    return {
      pubkey,
      status: getStatusFromPeerState(peer?.state),
      lastSeen: peer?.lastSeen ?? null,
      peerState: peer?.state,
    };
  }, [peerMap]);

  // Get presence for all friends
  const getFriendsPresence = useCallback((): FriendPresence[] => {
    return Array.from(friends).map(pubkey => getPresence(pubkey));
  }, [friends, getPresence]);

  // Check if a friend is online
  const isOnline = useCallback((pubkey: string): boolean => {
    const peer = peerMap.get(pubkey);
    return peer?.state === 'connected';
  }, [peerMap]);

  // Count online friends
  const onlineFriendsCount = useMemo(() => {
    let count = 0;
    for (const pubkey of friends) {
      const peer = peerMap.get(pubkey);
      if (peer?.state === 'connected') {
        count++;
      }
    }
    return count;
  }, [friends, peerMap]);

  return {
    getPresence,
    getFriendsPresence,
    isOnline,
    onlineFriendsCount,
  };
}
